import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CaretLeftIcon } from "@phosphor-icons/react";

// The overview page stores its last URL (page, filters, view) here so the account detail pages
// can always return to it in one click, no matter how many tabs the user switched in between.
const OVERVIEW_URL_KEY = "accounts-overview-url";
const DEFAULT_OVERVIEW_URL = "/accounts-overview";

export const rememberOverviewUrl = (url: string) => {
    try {
        sessionStorage.setItem(OVERVIEW_URL_KEY, url);
    } catch {
        /* storage unavailable */
    }
};

const getOverviewUrl = () => {
    try {
        return sessionStorage.getItem(OVERVIEW_URL_KEY) || DEFAULT_OVERVIEW_URL;
    } catch {
        return DEFAULT_OVERVIEW_URL;
    }
};

export const BackToOverviewButton: React.FC<{ className?: string }> = ({ className }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    return (
        <button type="button" onClick={() => navigate(getOverviewUrl())} className={className}>
            <CaretLeftIcon className="w-4 h-4" />
            {t("metrixLive.backToOverview")}
        </button>
    );
};

const todayParam = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

interface AccountDetailTabsProps {
    platform?: string;
    id?: string;
    active: "analysis" | "detail";
}

// Tabs switch with client-side navigation and `replace`, so switching between "Analysis" and
// "Details" neither reloads the page nor piles up browser-history entries.
export const AccountDetailTabs: React.FC<AccountDetailTabsProps> = ({ platform, id, active }) => {
    const { t } = useTranslation();
    const tabClass = (isActive: boolean) =>
        `${isActive ? "tab-active" : "tab-inactive"} whitespace-nowrap !no-underline !py-3 !px-1 text-sm flex items-center gap-2 transition-colors`;

    return (
        <nav className="-mb-px flex gap-4 sm:gap-8 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            <Link to={`/academy?tab=checklist&date=${todayParam()}&step=0`} className={tabClass(false)}>
                <svg className="max-sm:hidden w-4 h-4 !text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                {t("metrixLive.tabs.checklist")}
            </Link>
            <Link to={`/${platform}/account-metrix/${id}`} replace className={tabClass(active === "analysis")}>
                <svg className="max-sm:hidden w-4 h-4 !text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t("metrixLive.tabs.analysis")}
            </Link>
            <Link to={`/${platform}/account-live-metrix/${id}`} replace className={tabClass(active === "detail")}>
                <svg className="max-sm:hidden w-4 h-4 !text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                {t("metrixLive.tabs.detail")}
            </Link>
        </nav>
    );
};
